// Types are now imported from types/env.ts
import type { VercelEnv } from "../src/types/env";

// On the server, Vercel provides `VERCEL_ENV` directly. On the client, we
// fall back to `NEXT_PUBLIC_VERCEL_ENV` so the same module can be shared.
const vercelEnv = (process.env.VERCEL_ENV ||
  process.env.NEXT_PUBLIC_VERCEL_ENV ||
  "development") as VercelEnv;

// Client-safe environment variables (NEXT_PUBLIC_*)
// NOTE: Must use direct references (not dynamic keys) for webpack to inject them
export const clientEnv = {
  NEXT_PUBLIC_VERCEL_ENV: vercelEnv,
  NEXT_PUBLIC_COMMIT_SHA: process.env.NEXT_PUBLIC_COMMIT_SHA,
  NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
  NEXT_PUBLIC_ZAPIER_ORIGIN: process.env.NEXT_PUBLIC_ZAPIER_ORIGIN,
  NEXT_PUBLIC_FACEBOOK_APP_ID: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
  NEXT_PUBLIC_FACEBOOK_CONFIG_ID: process.env.NEXT_PUBLIC_FACEBOOK_CONFIG_ID,
  NEXT_PUBLIC_FACEBOOK_SDK_VERSION:
    process.env.NEXT_PUBLIC_FACEBOOK_SDK_VERSION || "v24.0",
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_DATADOG_APP_ID: process.env.NEXT_PUBLIC_DATADOG_APP_ID,
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN:
    process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_GTM_DEV_ID: process.env.NEXT_PUBLIC_GTM_DEV_ID,
  NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT_ID:
    process.env.NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT_ID,
  NEXT_PUBLIC_EVENTS_TOOLKIT_EMIT_API:
    process.env.NEXT_PUBLIC_EVENTS_TOOLKIT_EMIT_API,
} as const;

// Convenience exports
export const isProduction = vercelEnv === "production";
export const isDevelopment = vercelEnv === "development";
export const isPreview = vercelEnv === "preview";

export const referer = isProduction
  ? (clientEnv.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "https://zapier.com")
  : (clientEnv.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "https://zapier-staging.com");

export const baseUrl =
  clientEnv.NEXT_PUBLIC_ZAPIER_ORIGIN || clientEnv.NEXT_PUBLIC_BASE_URL;

// Fallback constant for OneTrust domain script ID (zapier.com production)
export const ONETRUST_DOMAIN_SCRIPT_ID_FALLBACK =
  "c2bc6bc2-2599-4efd-90da-5725af13c244";

// Simple validation for required environment variables
function validateEnvironment() {
  if (isProduction) {
    if (!process.env.NEXT_PUBLIC_FACEBOOK_APP_ID) {
      throw new Error(
        "Missing required production env: NEXT_PUBLIC_FACEBOOK_APP_ID",
      );
    }
    if (!process.env.NEXT_PUBLIC_FACEBOOK_CONFIG_ID) {
      throw new Error(
        "Missing required production env: NEXT_PUBLIC_FACEBOOK_CONFIG_ID",
      );
    }
    if (!process.env.NEXT_PUBLIC_ZAPIER_ORIGIN) {
      throw new Error(
        "Missing required production env: NEXT_PUBLIC_ZAPIER_ORIGIN",
      );
    }
  }

  if (isPreview) {
    if (!process.env.NEXT_PUBLIC_ZAPIER_ORIGIN) {
      console.warn(
        "⚠️ Missing staging environment variable: NEXT_PUBLIC_ZAPIER_ORIGIN",
      );
    }
  }
}

validateEnvironment();
